<template>
    <div class="space-y-3 pb-4">

        <!-- Chargement -->
        <div v-if="!loaded" class="flex items-center gap-2 text-slate-400 py-6 justify-center">
            <span class="w-4 h-4 border-2 border-slate-300 border-t-blue-500 rounded-full animate-spin" />
            <span class="text-sm">Chargement…</span>
        </div>

        <template v-else>

            <!-- ── Date ──────────────────────────────────────── -->
            <div class="bg-white rounded-2xl border border-gray-200 px-4 py-3">
                <label class="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2 block">
                    Date d'intervention
                </label>
                <input
                    v-model="form.date_intervention"
                    type="date"
                    class="w-full border border-gray-200 rounded-xl px-3 py-3 text-base outline-none focus:border-blue-500"
                />
            </div>

            <!-- ── Aucun point ──────────────────────────────── -->
            <div v-if="sections.length === 0" class="bg-white rounded-2xl border border-gray-200 px-4 py-6 text-sm text-slate-400 text-center">
                Aucun point de contrôle disponible pour cet appareil.
            </div>

            <!-- ── Sections ─────────────────────────────────── -->
            <div
                v-for="section in sections"
                :key="section.name"
                class="bg-white rounded-2xl border overflow-hidden"
                :class="sectionDone(section) ? 'border-green-200' : 'border-gray-200'"
            >
                <!-- Header section -->
                <div
                    class="flex items-center justify-between px-4 py-3.5 border-b"
                    :class="sectionDone(section)
                        ? 'bg-green-50 border-green-100'
                        : 'bg-slate-50 border-gray-100'"
                >
                    <div class="flex items-center gap-2.5 min-w-0">
                        <!-- Indicateur complétion -->
                        <span
                            class="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold"
                            :class="sectionDone(section)
                                ? 'bg-green-500 text-white'
                                : 'bg-white border-2 border-gray-200 text-slate-400'"
                        >
                            <svg v-if="sectionDone(section)" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M20 6L9 17l-5-5" stroke-linecap="round"/></svg>
                            <span v-else>{{ sectionAnswered(section) }}</span>
                        </span>
                        <div class="min-w-0">
                            <p class="text-sm font-bold"
                                :class="sectionDone(section) ? 'text-green-800' : 'text-slate-700'">
                                {{ section.name }}
                            </p>
                            <p class="text-xs"
                                :class="sectionDone(section) ? 'text-green-600' : 'text-slate-400'">
                                {{ sectionAnswered(section) }} / {{ section.points.length }} points
                            </p>
                        </div>
                    </div>

                    <!-- Bouton tout cocher — uniquement pour sections 100% checkbox -->
                    <button
                        v-if="sectionAllCheckbox(section)"
                        class="shrink-0 ml-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
                        :class="sectionDone(section)
                            ? 'bg-green-100 text-green-700 active:bg-green-200'
                            : 'bg-blue-600 text-white active:bg-blue-700'"
                        @click="toggleAllSection(section)"
                    >
                        {{ sectionDone(section) ? 'Décocher' : 'Tout ✓' }}
                    </button>
                </div>

                <!-- Liste des points — toujours visible, pas de dropdown -->
                <div class="divide-y divide-gray-100">
                    <div
                        v-for="point in section.points"
                        :key="point.key"
                        class="flex items-center gap-3 px-4 py-4"
                    >
                        <!-- Label -->
                        <span class="flex-1 text-sm leading-snug text-slate-700">
                            {{ point.label }}
                        </span>

                        <!-- Checkbox → grand toggle tactile -->
                        <button
                            v-if="point.type_input === 'checkbox'"
                            class="shrink-0 w-14 h-8 rounded-full relative transition-colors"
                            :class="form.points_controle[point.key] === true ? 'bg-blue-500' : 'bg-gray-200'"
                            @click="setPoint(point.key, form.points_controle[point.key] === true ? null : true)"
                        >
                            <div
                                class="w-6 h-6 rounded-full bg-white shadow absolute top-1 transition-all"
                                :class="form.points_controle[point.key] === true ? 'left-7' : 'left-1'"
                            />
                        </button>

                        <!-- Number input -->
                        <input
                            v-else-if="point.type_input === 'number'"
                            :value="form.points_controle[point.key] ?? ''"
                            type="text"
                            inputmode="decimal"
                            class="shrink-0 w-24 border border-gray-200 rounded-xl px-3 py-2.5 text-base outline-none focus:border-blue-500 text-right"
                            placeholder="—"
                            @input="setPoint(point.key, $event.target.value !== '' ? $event.target.value : null)"
                            @blur="normalizeNumber(point.key)"
                        />

                        <!-- Text fallback -->
                        <input
                            v-else
                            :value="form.points_controle[point.key] ?? ''"
                            type="text"
                            class="shrink-0 w-32 border border-gray-200 rounded-xl px-3 py-2.5 text-base outline-none focus:border-blue-500"
                            placeholder="—"
                            @input="setPoint(point.key, $event.target.value || null)"
                        />
                    </div>
                </div>
            </div>

            <!-- ── Commentaire ───────────────────────────────── -->
            <div class="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div class="px-4 py-3.5 bg-slate-50 border-b border-gray-100">
                    <p class="text-sm font-bold text-slate-700">Commentaire</p>
                    <p class="text-xs text-slate-400 mt-0.5">Appuyez sur un raccourci ou saisissez librement</p>
                </div>

                <!-- Presets — 2 colonnes, grands boutons -->
                <div class="px-4 pt-3 grid grid-cols-2 gap-2">
                    <button
                        v-for="p in commentPresets"
                        :key="p"
                        class="py-3.5 px-3 rounded-xl text-sm font-semibold text-center transition-colors border"
                        :class="form.commentaire_intervenant && form.commentaire_intervenant.includes(p)
                            ? 'bg-blue-50 border-blue-300 text-blue-700'
                            : 'bg-gray-50 border-gray-200 text-slate-600 active:bg-gray-100'"
                        @click="appendComment(p)"
                    >
                        {{ p }}
                    </button>
                </div>

                <!-- Textarea -->
                <div class="px-4 pt-3 pb-4">
                    <textarea
                        v-model="form.commentaire_intervenant"
                        class="w-full border border-gray-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-blue-500 resize-none text-slate-700"
                        rows="3"
                        placeholder="Commentaire libre…"
                    />
                    <button
                        v-if="form.commentaire_intervenant"
                        class="mt-1 text-xs text-slate-400 active:text-red-500 py-1"
                        @click="form = { ...form, commentaire_intervenant: '' }"
                    >
                        Effacer
                    </button>
                </div>
            </div>

        </template>
    </div>
</template>

<script>
export default {
    name: 'AttestationEntretienStep',
    inject: ['wm'],

    data() {
        return {
            loaded: false,

            form: {
                appareil_id: null,
                adresse_id: null,
                date_intervention: new Date().toISOString().split('T')[0],
                points_controle: {},
                commentaire_intervenant: '',
            },

            sections: [],

            commentPresets: ['RAS', 'Réglage effectué', 'Nettoyage complet', 'Pièce à commander'],
        };
    },

    computed: {
        STORAGE_KEY() {
            return `intervention:${this.wm.intervention.id}:attestation_entretien`;
        },
    },

    watch: {
        form: {
            deep: true,
            handler(val) {
                if (!this.loaded) return;
                try { localStorage.setItem(this.STORAGE_KEY, JSON.stringify(val)); } catch {}
            },
        },
    },

    created() {
        this.loadSections();
        this.loadFromStorage();
    },

    methods: {

        /* ────────────────────────────────────────────────
         * CHARGEMENT DES POINTS DE CONTRÔLE
         * ──────────────────────────────────────────────── */
        loadSections() {
            try {
                const raw = localStorage.getItem('mobilePointsControle');
                if (!raw) return;

                const allPoints   = JSON.parse(raw);
                const appType     = this.wm.intervention?.appareil?.type;
                const typeLogique = this.wm.intervention?.intervention_type?.type_logique;

                let pointsMap = null;

                if (appType && allPoints[appType]) {
                    const byType = allPoints[appType];
                    if (typeLogique && byType[typeLogique]) {
                        pointsMap = byType[typeLogique];
                    } else {
                        const firstKey = Object.keys(byType)[0];
                        if (firstKey) pointsMap = byType[firstKey];
                    }
                }

                // Fallback : structure directe { [section]: [points] }
                if (!pointsMap && allPoints && typeof allPoints === 'object') {
                    const firstVal = Object.values(allPoints)[0];
                    if (Array.isArray(firstVal)) pointsMap = allPoints;
                }

                if (!pointsMap) return;

                this.sections = Object.entries(pointsMap).map(([name, points]) => ({
                    name,
                    points: points.map(p => ({
                        key:        p.key,
                        label:      p.label,
                        type_input: p.type_input || 'checkbox',
                        section:    p.section || name,
                    })),
                }));

            } catch (e) {
                console.error('[AttestationEntretienStep] loadSections error', e);
            }
        },

        /* ────────────────────────────────────────────────
         * PERSISTANCE
         * ──────────────────────────────────────────────── */
        loadFromStorage() {
            const itvn = this.wm.intervention;
            this.form.appareil_id = itvn.client_appareil_id ?? null;
            this.form.adresse_id  = itvn.adresse_id ?? null;

            try {
                const stored = localStorage.getItem(this.STORAGE_KEY);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    this.form = {
                        ...this.form,
                        ...parsed,
                        points_controle: {
                            ...this.form.points_controle,
                            ...(parsed.points_controle || {}),
                        },
                    };
                }
            } catch {}

            this.loaded = true;
        },

        /* ────────────────────────────────────────────────
         * ACTIONS
         * ──────────────────────────────────────────────── */
        setPoint(key, value) {
            this.form = {
                ...this.form,
                points_controle: { ...this.form.points_controle, [key]: value },
            };
        },

        toggleAllSection(section) {
            const allDone = this.sectionDone(section);
            const patch   = {};
            section.points.forEach(p => { patch[p.key] = allDone ? null : true; });
            this.form = {
                ...this.form,
                points_controle: { ...this.form.points_controle, ...patch },
            };
        },

        // Convertit la string en number propre au blur — évite NaN dans le localStorage pendant la saisie
        normalizeNumber(key) {
            const raw = this.form.points_controle[key];
            if (raw === null || raw === undefined || raw === '') return;
            const n = parseFloat(String(raw).replace(',', '.'));
            this.setPoint(key, isNaN(n) ? null : n);
        },

        appendComment(text) {
            if (!this.form.commentaire_intervenant) {
                this.form = { ...this.form, commentaire_intervenant: text };
            } else if (!this.form.commentaire_intervenant.includes(text)) {
                this.form = { ...this.form, commentaire_intervenant: this.form.commentaire_intervenant + ' – ' + text };
            }
        },

        /* ────────────────────────────────────────────────
         * HELPERS
         * ──────────────────────────────────────────────── */
        sectionAnswered(section) {
            return section.points.filter(p => this.form.points_controle[p.key] != null).length;
        },

        sectionDone(section) {
            return section.points.length > 0 &&
                section.points.every(p => this.form.points_controle[p.key] != null);
        },

        sectionAllCheckbox(section) {
            return section.points.every(p => p.type_input === 'checkbox');
        },

        /* ────────────────────────────────────────────────
         * PAYLOAD FINAL
         * ──────────────────────────────────────────────── */
        getPayload() {
            const points_controle = this.sections.flatMap(s =>
                s.points.map(p => ({
                    key:     p.key,
                    label:   p.label,
                    section: p.section,
                    value:   this.form.points_controle[p.key] ?? null,
                }))
            );
            return { ...this.form, points_controle };
        },
    },
};
</script>